import { pointOnSegment, segmentsOf, type PathPoint, type Subpath, type VectorPath } from '@flobi/artwork-core';

/**
 * Simplify points: fewer anchors, the same shape.
 *
 * A traced or painted outline arrives as hundreds of corners a few pixels apart, some on
 * top of each other, and every one of them is a place the edge can wobble. This lays the
 * outline out as the line it actually draws, finds the corners that are really corners,
 * and fits the stretches between them with as few smooth curves as stay within a hair of
 * the original — Schneider's fitter, the one every vector tool's "simplify" is built on.
 * The order of the points is kept, so a hole stays a hole under either fill rule.
 */

interface Vec {
  x: number;
  y: number;
}

interface Cubic {
  p0: Vec;
  c1: Vec;
  c2: Vec;
  p3: Vec;
}

export interface SimplifyOptions {
  /**
   * How far the new outline may stray from the old, in the path's own units. Left out, it
   * follows the shape's size, so a logo and a poster-sized letter simplify alike.
   */
  readonly tolerance?: number;
}

/** A turn sharper than this, seen from a little way either side, stays a corner. */
const CORNER_DEGREES = 50;

/** The whole path, every subpath simplified on its own. */
export function simplifyVectorPath(path: VectorPath, options: SimplifyOptions = {}): VectorPath {
  const tolerance = options.tolerance ?? toleranceFor(path);
  return { ...path, subpaths: path.subpaths.map((subpath) => simplifySubpath(subpath, tolerance)) };
}

/** How many anchors a path has, which is what the menu reports a simplify by. */
export function anchorCount(path: VectorPath): number {
  return path.subpaths.reduce((sum, subpath) => sum + subpath.points.length, 0);
}

/** A quarter of a percent of the shape's diagonal: invisible at any zoom anybody works at. */
export function toleranceFor(path: VectorPath): number {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const subpath of path.subpaths) {
    for (const point of subpath.points) {
      minX = Math.min(minX, point.x);
      minY = Math.min(minY, point.y);
      maxX = Math.max(maxX, point.x);
      maxY = Math.max(maxY, point.y);
    }
  }
  if (!Number.isFinite(minX)) return 0.5;
  return Math.max(0.05, Math.hypot(maxX - minX, maxY - minY) * 0.0025);
}

/**
 * One outline, fitted again. Gives the subpath back as it was when there is nothing to
 * gain: a triangle has no points to spare, and a fit that came out longer is no fit.
 */
export function simplifySubpath(subpath: Subpath, tolerance: number): Subpath {
  const { points, closed } = subpath;
  if (points.length < (closed ? 4 : 3) || !(tolerance > 0)) return subpath;

  const line = dedupe(flatten(points, closed, tolerance), closed, tolerance * 0.5);
  if (line.length < (closed ? 3 : 2)) return subpath;

  const corners = cornersOf(line, closed, tolerance * 3);
  const cubics: Cubic[] = [];
  const cornerAt: boolean[] = [];

  if (closed && corners.length === 0) {
    // A loop with no corner: fitted from one place round and back to it, with the same
    // direction leaving as arriving so the seam does not show.
    const loop = [...line, line[0]!];
    const tangent = normalise(sub(line[1]!, line[line.length - 1]!));
    fitCubic(loop, tangent, scale(tangent, -1), tolerance * tolerance, cubics);
    cornerAt.push(...cubics.map(() => false));
  } else {
    const stops = closed ? corners : [0, ...corners.filter((index) => index > 0 && index < line.length - 1), line.length - 1];
    const pieces = closed ? stops.length : stops.length - 1;
    for (let piece = 0; piece < pieces; piece++) {
      const from = stops[piece]!;
      const to = stops[(piece + 1) % stops.length]!;
      const run = to > from ? line.slice(from, to + 1) : [...line.slice(from), ...line.slice(0, to + 1)];
      if (run.length < 2) continue;
      const before = cubics.length;
      fitCubic(run, leaving(run, tolerance * 4), arriving(run, tolerance * 4), tolerance * tolerance, cubics, true);
      // The first anchor of each piece is a corner; the joins the fitter made inside a
      // piece are smooth, because it split them along one tangent.
      for (let index = before; index < cubics.length; index++) cornerAt.push(index === before);
    }
    if (!closed) cornerAt.push(true);
  }

  if (cubics.length === 0) return subpath;
  const out = toPoints(cubics, cornerAt, closed, tolerance);
  return out.length < points.length ? { ...subpath, points: out } : subpath;
}

// ── laying the outline out ─────────────────────────────────────────────

/** The line the path draws, as close-set points: curves sampled, straights as their ends. */
function flatten(points: readonly PathPoint[], closed: boolean, tolerance: number): Vec[] {
  const out: Vec[] = [];
  for (const segment of segmentsOf(points, closed)) {
    out.push({ x: segment.p0.x, y: segment.p0.y });
    const curved = !onLine(segment.c1, segment.p0, segment.p3, 1e-6) || !onLine(segment.c2, segment.p0, segment.p3, 1e-6);
    if (!curved) continue;
    const reach = distance(segment.p0, segment.c1) + distance(segment.c1, segment.c2) + distance(segment.c2, segment.p3);
    const steps = Math.min(64, Math.max(4, Math.ceil(reach / Math.max(tolerance, 1e-6))));
    for (let step = 1; step < steps; step++) out.push(pointOnSegment(segment, step / steps));
  }
  if (!closed) {
    const last = points[points.length - 1]!;
    out.push({ x: last.x, y: last.y });
  }
  return out;
}

/** Points on top of points — the clusters a tracer leaves — merged into one. */
function dedupe(line: Vec[], closed: boolean, within: number): Vec[] {
  const out: Vec[] = [];
  for (const point of line) {
    if (out.length && distance(out[out.length - 1]!, point) < within) continue;
    out.push(point);
  }
  // An open line keeps its own last point, whatever was near it: that is where it ends.
  if (!closed && line.length && out[out.length - 1] !== line[line.length - 1]) {
    if (out.length > 1) out.pop();
    out.push(line[line.length - 1]!);
  }
  while (closed && out.length > 2 && distance(out[0]!, out[out.length - 1]!) < within) out.pop();
  return out;
}

/**
 * Where the line really turns. Each point is judged by the direction the line comes in
 * from a little way back and leaves in a little way on, not by its two neighbours, so a
 * jagged run of a traced edge reads as the straight it is and a tight round cap as the
 * curve it is.
 */
function cornersOf(line: readonly Vec[], closed: boolean, window: number): number[] {
  const out: number[] = [];
  const limit = Math.cos((CORNER_DEGREES * Math.PI) / 180);
  for (let index = closed ? 0 : 1; index < (closed ? line.length : line.length - 1); index++) {
    const here = line[index]!;
    const back = walk(line, closed, index, -1, window);
    const on = walk(line, closed, index, 1, window);
    if (!back || !on) continue;
    const a = normalise(sub(here, back));
    const b = normalise(sub(on, here));
    if (dot(a, b) >= limit) continue;
    // Of a few neighbours that all see the same turn, the sharpest one is the corner.
    const previous = out[out.length - 1];
    if (previous !== undefined && index - previous <= 2) {
      if (turn(line, closed, index, window) > turn(line, closed, previous, window)) out[out.length - 1] = index;
      continue;
    }
    out.push(index);
  }
  // On a loop the last corner can be the first one seen from the other side.
  if (closed && out.length > 1 && out[0]! + line.length - out[out.length - 1]! <= 2) {
    if (turn(line, closed, out[out.length - 1]!, window) > turn(line, closed, out[0]!, window)) out[0] = out[out.length - 1]!;
    out.pop();
  }
  return out;
}

function turn(line: readonly Vec[], closed: boolean, index: number, window: number): number {
  const back = walk(line, closed, index, -1, window);
  const on = walk(line, closed, index, 1, window);
  if (!back || !on) return 0;
  return 1 - dot(normalise(sub(line[index]!, back)), normalise(sub(on, line[index]!)));
}

/** The point a given distance along the line from an index, or as far as the line goes. */
function walk(line: readonly Vec[], closed: boolean, from: number, step: 1 | -1, reach: number): Vec | null {
  let at = from;
  let gone = 0;
  let last = line[from]!;
  for (let count = 1; count < line.length; count++) {
    const next = at + step;
    if (!closed && (next < 0 || next >= line.length)) break;
    at = (next + line.length) % line.length;
    const point = line[at]!;
    const piece = distance(last, point);
    if (gone + piece >= reach) {
      const t = (reach - gone) / piece;
      return { x: last.x + (point.x - last.x) * t, y: last.y + (point.y - last.y) * t };
    }
    gone += piece;
    last = point;
  }
  return gone > 0 ? last : null;
}

/**
 * The direction a piece leaves its first point in, judged a little way along rather than
 * from the next point, which on a traced edge is mostly the tracer's jitter.
 */
function leaving(run: readonly Vec[], reach: number): Vec {
  const ahead = walk(run, false, 0, 1, Math.min(reach, length(run) / 3));
  return normalise(sub(ahead ?? run[1]!, run[0]!));
}

function arriving(run: readonly Vec[], reach: number): Vec {
  const last = run.length - 1;
  const behind = walk(run, false, last, -1, Math.min(reach, length(run) / 3));
  return normalise(sub(behind ?? run[last - 1]!, run[last]!));
}

function length(run: readonly Vec[]): number {
  let total = 0;
  for (let index = 1; index < run.length; index++) total += distance(run[index - 1]!, run[index]!);
  return total;
}

// ── Schneider's fitter (Graphics Gems, 1990) ───────────────────────────

function fitCubic(points: readonly Vec[], leave: Vec, arrive: Vec, error: number, out: Cubic[], cornered = false, depth = 0): void {
  const first = points[0]!;
  const last = points[points.length - 1]!;
  if (points.length === 2) {
    const third = distance(first, last) / 3;
    out.push({ p0: first, c1: add(first, scale(leave, third)), c2: add(last, scale(arrive, third)), p3: last });
    return;
  }
  // A whole run from corner to corner that never leaves the straight between them by more
  // than the tolerance is that straight: a traced edge's jitter is not a curve to keep.
  // Only between corners — anywhere else a straight would put a kink in a smooth curve.
  if (depth === 0 && cornered && points.every((point) => onLine(point, first, last, Math.sqrt(error)))) {
    out.push({ p0: first, c1: lerp(first, last, 1 / 3), c2: lerp(first, last, 2 / 3), p3: last });
    return;
  }

  let u = chordLengths(points);
  let cubic = generate(points, u, leave, arrive);
  let worst = maxError(points, cubic, u);
  if (worst.error < error) {
    out.push(cubic);
    return;
  }
  if (worst.error < error * 4) {
    for (let pass = 0; pass < 4; pass++) {
      u = reparameterise(points, u, cubic);
      cubic = generate(points, u, leave, arrive);
      worst = maxError(points, cubic, u);
      if (worst.error < error) {
        out.push(cubic);
        return;
      }
    }
  }
  // Too deep to be worth another split: the points themselves, joined, are the answer.
  if (depth > 24) {
    for (let index = 1; index < points.length; index++) {
      const a = points[index - 1]!;
      const b = points[index]!;
      out.push({ p0: a, c1: lerp(a, b, 1 / 3), c2: lerp(a, b, 2 / 3), p3: b });
    }
    return;
  }
  const split = Math.min(points.length - 2, Math.max(1, worst.index));
  const centre = normalise(sub(points[split - 1]!, points[split + 1]!));
  fitCubic(points.slice(0, split + 1), leave, centre, error, out, false, depth + 1);
  fitCubic(points.slice(split), scale(centre, -1), arrive, error, out, false, depth + 1);
}

/** The handles that best carry a curve through the points, along the tangents given. */
function generate(points: readonly Vec[], u: readonly number[], leave: Vec, arrive: Vec): Cubic {
  const first = points[0]!;
  const last = points[points.length - 1]!;
  let c00 = 0;
  let c01 = 0;
  let c11 = 0;
  let x0 = 0;
  let x1 = 0;
  for (let index = 0; index < points.length; index++) {
    const t = u[index]!;
    const s = 1 - t;
    const b0 = s * s * s;
    const b1 = 3 * t * s * s;
    const b2 = 3 * t * t * s;
    const b3 = t * t * t;
    const a0 = scale(leave, b1);
    const a1 = scale(arrive, b2);
    c00 += dot(a0, a0);
    c01 += dot(a0, a1);
    c11 += dot(a1, a1);
    const rest = sub(points[index]!, add(scale(first, b0 + b1), scale(last, b2 + b3)));
    x0 += dot(a0, rest);
    x1 += dot(a1, rest);
  }
  const det = c00 * c11 - c01 * c01;
  let alphaL = det === 0 ? 0 : (x0 * c11 - c01 * x1) / det;
  let alphaR = det === 0 ? 0 : (c00 * x1 - x0 * c01) / det;
  const span = distance(first, last);
  const tiny = 1e-6 * span;
  // A handle pointing backwards, or of no length, is the fit giving up: the third-of-the-way
  // guess is always a curve that at least goes the right way.
  if (alphaL < tiny || alphaR < tiny) {
    alphaL = span / 3;
    alphaR = span / 3;
  }
  return { p0: first, c1: add(first, scale(leave, alphaL)), c2: add(last, scale(arrive, alphaR)), p3: last };
}

function chordLengths(points: readonly Vec[]): number[] {
  const u = [0];
  for (let index = 1; index < points.length; index++) u.push(u[index - 1]! + distance(points[index]!, points[index - 1]!));
  const total = u[u.length - 1]!;
  return total > 0 ? u.map((value) => value / total) : u.map((_, index) => index / (points.length - 1));
}

/** One Newton step for each point towards where on the curve it is nearest. */
function reparameterise(points: readonly Vec[], u: readonly number[], cubic: Cubic): number[] {
  return u.map((t, index) => {
    const at = bezier(cubic, t);
    const d1 = derivative(cubic, t);
    const d2 = second(cubic, t);
    const diff = sub(at, points[index]!);
    const numerator = dot(diff, d1);
    const denominator = dot(d1, d1) + dot(diff, d2);
    if (Math.abs(denominator) < 1e-12) return t;
    return Math.min(1, Math.max(0, t - numerator / denominator));
  });
}

function maxError(points: readonly Vec[], cubic: Cubic, u: readonly number[]): { error: number; index: number } {
  let error = 0;
  let index = Math.floor(points.length / 2);
  for (let at = 1; at < points.length - 1; at++) {
    const away = squared(sub(bezier(cubic, u[at]!), points[at]!));
    if (away > error) {
      error = away;
      index = at;
    }
  }
  return { error, index };
}

// ── back to anchors ────────────────────────────────────────────────────

/**
 * The curves as anchors. A handle lying on the straight between its two anchors is
 * dropped, so a straight edge stays a straight edge rather than a curve that happens to
 * be flat; an anchor keeps `smooth` only where both its handles survive.
 */
function toPoints(cubics: readonly Cubic[], cornerAt: readonly boolean[], closed: boolean, tolerance: number): PathPoint[] {
  // Between two corners, handles lying on the straight are a straight. Anywhere else
  // dropping them would kink a smooth curve, so they stay.
  const flat = cubics.map((cubic, index) => {
    const ends = cornerAt[index] && (index + 1 < cornerAt.length ? cornerAt[index + 1] : closed && cornerAt[0]);
    return !!ends && onLine(cubic.c1, cubic.p0, cubic.p3, tolerance * 0.1) && onLine(cubic.c2, cubic.p0, cubic.p3, tolerance * 0.1);
  });
  const anchors = closed ? cubics.length : cubics.length + 1;
  const out: PathPoint[] = [];
  for (let index = 0; index < anchors; index++) {
    const leavingCubic = index < cubics.length ? cubics[index] : undefined;
    const arrivingIndex = index > 0 ? index - 1 : closed ? cubics.length - 1 : -1;
    const arrivingCubic = arrivingIndex >= 0 ? cubics[arrivingIndex] : undefined;
    const at = leavingCubic ? leavingCubic.p0 : arrivingCubic!.p3;
    const handleIn = arrivingCubic && !flat[arrivingIndex] ? pair(arrivingCubic.c2) : undefined;
    const handleOut = leavingCubic && !flat[index] ? pair(leavingCubic.c1) : undefined;
    const smooth = !cornerAt[index] && !!handleIn && !!handleOut;
    out.push({
      x: at.x,
      y: at.y,
      ...(handleIn ? { in: handleIn } : {}),
      ...(handleOut ? { out: handleOut } : {}),
      kind: smooth ? 'smooth' : 'corner',
    });
  }
  return out;
}

function pair(at: Vec): [number, number] {
  return [at.x, at.y];
}

// ── vectors ────────────────────────────────────────────────────────────

function add(a: Vec, b: Vec): Vec {
  return { x: a.x + b.x, y: a.y + b.y };
}

function sub(a: Vec, b: Vec): Vec {
  return { x: a.x - b.x, y: a.y - b.y };
}

function scale(a: Vec, by: number): Vec {
  return { x: a.x * by, y: a.y * by };
}

function dot(a: Vec, b: Vec): number {
  return a.x * b.x + a.y * b.y;
}

function squared(a: Vec): number {
  return a.x * a.x + a.y * a.y;
}

function distance(a: Vec, b: Vec): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function lerp(a: Vec, b: Vec, t: number): Vec {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

function normalise(a: Vec): Vec {
  const length = Math.hypot(a.x, a.y);
  return length > 1e-12 ? { x: a.x / length, y: a.y / length } : { x: 1, y: 0 };
}

/** Whether a control lies on the straight between two anchors, within a distance. */
function onLine(point: Vec, a: Vec, b: Vec, within: number): boolean {
  const ab = sub(b, a);
  const length = Math.hypot(ab.x, ab.y);
  if (length < 1e-12) return distance(point, a) <= within;
  const t = dot(sub(point, a), ab) / (length * length);
  if (t < -0.01 || t > 1.01) return false;
  return Math.abs(ab.x * (point.y - a.y) - ab.y * (point.x - a.x)) / length <= within;
}

function bezier(cubic: Cubic, t: number): Vec {
  const s = 1 - t;
  const a = s * s * s;
  const b = 3 * s * s * t;
  const c = 3 * s * t * t;
  const d = t * t * t;
  return {
    x: a * cubic.p0.x + b * cubic.c1.x + c * cubic.c2.x + d * cubic.p3.x,
    y: a * cubic.p0.y + b * cubic.c1.y + c * cubic.c2.y + d * cubic.p3.y,
  };
}

function derivative(cubic: Cubic, t: number): Vec {
  const s = 1 - t;
  const a = sub(cubic.c1, cubic.p0);
  const b = sub(cubic.c2, cubic.c1);
  const c = sub(cubic.p3, cubic.c2);
  return {
    x: 3 * (s * s * a.x + 2 * s * t * b.x + t * t * c.x),
    y: 3 * (s * s * a.y + 2 * s * t * b.y + t * t * c.y),
  };
}

function second(cubic: Cubic, t: number): Vec {
  const s = 1 - t;
  const a = add(sub(cubic.c2, scale(cubic.c1, 2)), cubic.p0);
  const b = add(sub(cubic.p3, scale(cubic.c2, 2)), cubic.c1);
  return { x: 6 * (s * a.x + t * b.x), y: 6 * (s * a.y + t * b.y) };
}
